/**
 * Helpers visuales compartidos entre las 8 secciones renderizadas como
 * página A4. Todas estas piezas usan CSS vars derivadas del branding del
 * coach (`--al-accent`, `--al-bg`, `--al-ink`) inyectadas por el contenedor
 * del preview.
 */
import { createContext, useContext, type CSSProperties, type ReactNode } from "react";
import type { PlanBrand, PlanDecoration } from "../../../types/plan";
import { DecorationsLayer } from "../DecorationsLayer";

/**
 * Contexto para que `<SectionPage>` pueda leer el `brand` del plan sin
 * recibirlo por prop desde cada sección. Lo provee `<SectionView>` una sola
 * vez para todo el árbol. Si no hay provider, las secciones renderizan sin
 * marca de agua (caso de tests / preview de plantilla sin coach).
 */
export const SectionBrandContext = createContext<PlanBrand | null>(null);

/**
 * Contexto de decoraciones (íconos flotantes tipo Canva) por sección.
 * `SectionView` lo provee por cada sección con su `decorations` actual +
 * handler de cambio. Si no hay provider o readonly + array vacío, el
 * `DecorationsLayer` no renderiza nada.
 */
export interface SectionDecorationsValue {
  decorations: PlanDecoration[];
  /** null en readonly (vista pública / PDF). */
  onChange: ((next: PlanDecoration[]) => void) | null;
}
export const SectionDecorationsContext = createContext<SectionDecorationsValue | null>(null);

export interface SectionPageProps {
  children: ReactNode;
  /** Si true (modo público), no se muestra el outline del A4 hover. */
  readonly?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** Wrapper A4 (1:1.414) para una sección. Renderiza marca de agua del coach
 *  encima del contenido (con opacidad baja, sin interferir clicks) cuando el
 *  brand tiene `watermark=true && logoUrl`. Pensado para prevenir plagio del
 *  link público del plan que el coach manda a sus clientes.
 *
 *  Si el contexto provee decoraciones (íconos Canva flotantes), se renderiza
 *  el `<DecorationsLayer>` encima del contenido pero debajo del watermark
 *  visual. En modo readonly solo render; en edit, drag/resize/delete. */
export function SectionPage({ children, readonly, className, style }: SectionPageProps) {
  const brand = useContext(SectionBrandContext);
  const decoCtx = useContext(SectionDecorationsContext);
  const showWatermark = !!(brand?.watermark && brand?.logoUrl);
  const decorations = decoCtx?.decorations || [];
  const showDecorations = decorations.length > 0 || (!readonly && decoCtx?.onChange);
  return (
    <div
      className={"al-page " + (readonly ? "al-page-public " : "") + (className || "")}
      style={style}
    >
      {children}
      {showWatermark && (
        <div className="al-pg-watermark" aria-hidden="true">
          <img src={brand!.logoUrl!} alt="" />
        </div>
      )}
      {showDecorations && (
        <DecorationsLayer
          decorations={decorations}
          readonly={readonly || !decoCtx?.onChange}
          onChange={decoCtx?.onChange || undefined}
          accentColor={brand?.accent}
        />
      )}
    </div>
  );
}

interface PageHeaderProps {
  eyebrow: string;
  title: string;
  sub?: string;
}

export function PageHeader({ eyebrow, title, sub }: PageHeaderProps) {
  return (
    <header className="al-pg-header">
      <div className="al-pg-eyebrow">— {eyebrow.toUpperCase()}</div>
      <h2 className="al-pg-title">
        {title}
        {sub ? <span className="al-pg-title-sub"> · {sub}</span> : null}
      </h2>
    </header>
  );
}

interface FieldBlockProps {
  label: string;
  children: ReactNode;
}

/** Field con label + slot editable. */
export function FieldBlock({ label, children }: FieldBlockProps) {
  return (
    <div className="al-pg-field">
      <div className="al-pg-field-lbl">{label}</div>
      <div className="al-pg-field-val">{children}</div>
    </div>
  );
}

/** Hook para consumir el brand del contexto desde subcomponentes (ej. paneles
 *  flotantes). */
export function useSectionBrand(): PlanBrand | null {
  return useContext(SectionBrandContext);
}
